import React, { useState, useEffect } from 'react';
import { csAdminApi } from '../../services/api';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG } from '../../constants/statusColors';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Alert,
  InputAdornment,
  Chip,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  EventOutlined as EventIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  VideoCall as VideoCallIcon,
} from '@mui/icons-material';

const MEETING_TYPES = [
  { value: 'regular', label: 'Regular Meeting' },
  { value: 'special', label: 'Special Meeting' },
  { value: 'emergency', label: 'Emergency Meeting' },
  { value: 'annual', label: 'Annual Meeting' },
  { value: 'quarterly', label: 'Quarterly Review' },
];

const MEETING_LOCATIONS = [
  { value: 'boardroom', label: 'Main Boardroom' },
  { value: 'conference_a', label: 'Conference Room A' },
  { value: 'conference_b', label: 'Conference Room B' },
  { value: 'virtual', label: 'Virtual (Online)' },
  { value: 'hybrid', label: 'Hybrid (In-person + Virtual)' },
  { value: 'other', label: 'Other Location' },
];

const ScheduleMeetingModal = ({ 
  open, 
  onClose, 
  onSchedule, 
  committeeId, 
  committeeName,
  members = [],
  editMeeting = null 
}) => {
  const [formData, setFormData] = useState({
    date: '',
    startTime: '09:00',
    endTime: '10:00',
    type: 'regular',
    location: 'boardroom',
    customLocation: '',
    virtualLink: '',
    agenda: '',
    notes: '',
  });
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isEditing = !!editMeeting;

  // Helper to get type value from label
  const getTypeValue = (typeLabel) => {
    if (!typeLabel) return 'regular';
    const found = MEETING_TYPES.find(t => 
      t.label.toLowerCase() === typeLabel.toLowerCase() ||
      t.value === typeLabel
    );
    return found ? found.value : 'regular';
  };

  // Helper to get location value from label
  const getLocationValue = (locationLabel) => {
    if (!locationLabel) return 'boardroom';
    const found = MEETING_LOCATIONS.find(l => 
      l.label.toLowerCase() === locationLabel.toLowerCase() ||
      l.value === locationLabel
    );
    return found ? found.value : 'other';
  };

  // Initialize form with edit data
  useEffect(() => {
    if (open && editMeeting) {
      // Format date for input[type="date"]
      let formattedDate = '';
      if (editMeeting.date) {
        try {
          const dateObj = new Date(editMeeting.date);
          formattedDate = dateObj.toISOString().split('T')[0];
        } catch {
          formattedDate = editMeeting.date;
        }
      }

      const typeValue = editMeeting.typeCode || getTypeValue(editMeeting.type);
      const locationValue = editMeeting.locationCode || getLocationValue(editMeeting.location);
      const isCustomLocation = locationValue === 'other' && editMeeting.location;

      setFormData({
        date: formattedDate,
        startTime: editMeeting.startTime || '09:00',
        endTime: editMeeting.endTime || '10:00',
        type: typeValue,
        location: locationValue,
        customLocation: isCustomLocation ? editMeeting.location : (editMeeting.customLocation || ''),
        virtualLink: editMeeting.virtualLink || '',
        agenda: editMeeting.agenda || '',
        notes: editMeeting.notes || '',
      });
      setSelectedAttendees(editMeeting.attendeeIds || []);
    } else if (open && !editMeeting) {
      // Reset form for new meeting
      setFormData({
        date: '',
        startTime: '09:00',
        endTime: '10:00',
        type: 'regular',
        location: 'boardroom',
        customLocation: '',
        virtualLink: '',
        agenda: '',
        notes: '',
      });
    }
  }, [open, editMeeting]);

  // Pre-select all members by default for new meetings
  useEffect(() => {
    if (open && !isEditing && members.length > 0) {
      setSelectedAttendees(members.map(m => m.id));
    }
  }, [open, isEditing, members]);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError(null);
  };

  const handleToggleAttendee = (memberId) => {
    setSelectedAttendees(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAttendees.length === members.length) {
      setSelectedAttendees([]);
    } else {
      setSelectedAttendees(members.map(m => m.id));
    }
  };

  const validateForm = () => {
    if (!formData.date) {
      setError('Please select a meeting date');
      return false;
    }
    if (!formData.startTime || !formData.endTime) {
      setError('Please set meeting start and end times');
      return false;
    }
    if (formData.startTime >= formData.endTime) {
      setError('End time must be after start time');
      return false;
    }
    if (!formData.type) {
      setError('Please select a meeting type');
      return false;
    }
    if (!formData.location) {
      setError('Please select a location');
      return false;
    }
    if (formData.location === 'other' && !formData.customLocation.trim()) {
      setError('Please specify the meeting location');
      return false;
    }
    if ((formData.location === 'virtual' || formData.location === 'hybrid') && !formData.virtualLink.trim()) {
      setError('Please provide a virtual meeting link');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setError(null);

    try {
      const apiPayload = {
        meetingDate: formData.date,
        meetingType: MEETING_TYPES.find(t => t.value === formData.type)?.label || formData.type,
        status: editMeeting?.status || 'SCHEDULED',
        notes: formData.agenda?.trim() || formData.notes?.trim() || null,
      };
      if (committeeId) {
        if (isEditing && editMeeting?.id) {
          await csAdminApi.updateCommitteeMeeting(committeeId, editMeeting.id, apiPayload);
        } else {
          await csAdminApi.scheduleCommitteeMeeting(committeeId, apiPayload);
        }
      }

      const locationLabel = formData.location === 'other'
        ? formData.customLocation 
        : MEETING_LOCATIONS.find(l => l.value === formData.location)?.label;

      const meetingData = {
        id: editMeeting?.id || Date.now(),
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        type: MEETING_TYPES.find(t => t.value === formData.type)?.label,
        typeCode: formData.type,
        location: locationLabel,
        locationCode: formData.location,
        virtualLink: formData.virtualLink,
        agenda: formData.agenda.trim(),
        notes: formData.notes.trim(),
        attendeeIds: selectedAttendees,
        attendees: selectedAttendees.length,
        status: 'SCHEDULED',
        quorumMet: selectedAttendees.length >= Math.ceil(members.length / 2),
      };

      if (onSchedule) {
        onSchedule(meetingData);
      }

      handleClose();
    } catch (err) {
      setError('Failed to schedule meeting. Please try again.');
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      date: '',
      startTime: '09:00',
      endTime: '10:00',
      type: 'regular',
      location: 'boardroom',
      customLocation: '',
      virtualLink: '',
      agenda: '',
      notes: '',
    });
    setSelectedAttendees([]);
    setError(null);
    onClose();
  };

  const quorumRequired = Math.ceil(members.length / 2);
  const quorumMet = selectedAttendees.length >= quorumRequired;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon color="primary" />
            <Typography variant="h6" component="div">
              {isEditing ? 'Edit Meeting' : 'Schedule Meeting'}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ color: 'grey.500' }}
            disabled={submitting}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Committee Info */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Scheduling meeting for:
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {committeeName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {members.length} members • Quorum: {quorumRequired} members
          </Typography>
        </Box>

        {/* Date and Time */}
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Date & Time
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            required
            label="Meeting Date"
            type="date"
            value={formData.date}
            onChange={handleChange('date')}
            InputLabelProps={{ shrink: true }}
            disabled={submitting}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
          />
          <TextField
            fullWidth
            required
            label="Start Time"
            type="time"
            value={formData.startTime}
            onChange={handleChange('startTime')}
            InputLabelProps={{ shrink: true }}
            disabled={submitting}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TimeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            required
            label="End Time"
            type="time"
            value={formData.endTime}
            onChange={handleChange('endTime')}
            InputLabelProps={{ shrink: true }}
            disabled={submitting}
          />
        </Box>

        {/* Meeting Type and Location */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <FormControl fullWidth required>
            <InputLabel>Meeting Type</InputLabel>
            <Select
              value={formData.type}
              label="Meeting Type"
              onChange={handleChange('type')}
              disabled={submitting}
            >
              {MEETING_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth required>
            <InputLabel>Location</InputLabel>
            <Select
              value={formData.location}
              label="Location"
              onChange={handleChange('location')}
              disabled={submitting}
            >
              {MEETING_LOCATIONS.map((loc) => (
                <MenuItem key={loc.value} value={loc.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {loc.value === 'virtual' ? (
                      <VideoCallIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    ) : (
                      <LocationIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    )}
                    {loc.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Custom Location or Virtual Link */}
        {formData.location === 'other' && (
          <TextField
            fullWidth
            required
            label="Specify Location"
            value={formData.customLocation}
            onChange={handleChange('customLocation')}
            disabled={submitting}
            placeholder="Enter meeting location"
            sx={{ mb: 3 }}
          />
        )}

        {(formData.location === 'virtual' || formData.location === 'hybrid') && (
          <TextField
            fullWidth
            required
            label="Virtual Meeting Link"
            value={formData.virtualLink}
            onChange={handleChange('virtualLink')}
            disabled={submitting}
            placeholder="https://teams.microsoft.com/..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <VideoCallIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
        )}

        <Divider sx={{ my: 3 }} />

        {/* Attendees */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Attendees ({selectedAttendees.length}/{members.length})
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={quorumMet ? 'Quorum Met' : 'Quorum Not Met'}
                size="small"
                sx={{
                  bgcolor: quorumMet ? ACTIVE_GREEN_BG : '#FFF3E0',
                  color: quorumMet ? ACTIVE_GREEN : '#E65100',
                  fontWeight: 600,
                }}
              />
              <Button size="small" onClick={handleSelectAll}>
                {selectedAttendees.length === members.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>
          </Box>

          <List
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              maxHeight: 200,
              overflow: 'auto',
              bgcolor: 'background.paper',
            }}
          >
            {members.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="No members in this committee"
                  sx={{ textAlign: 'center', py: 2 }}
                />
              </ListItem>
            ) : (
              members.map((member) => (
                <ListItem
                  key={member.id}
                  button
                  onClick={() => handleToggleAttendee(member.id)}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedAttendees.includes(member.id)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={member.fullName || member.name}
                    secondary={member.role || member.position}
                  />
                </ListItem>
              ))
            )}
          </List>
        </Box>

        {/* Agenda */}
        <TextField
          fullWidth
          label="Meeting Agenda"
          value={formData.agenda}
          onChange={handleChange('agenda')}
          multiline
          rows={3}
          placeholder="Enter the meeting agenda items..."
          disabled={submitting}
          sx={{ mb: 3 }}
        />

        {/* Notes */}
        <TextField
          fullWidth
          label="Additional Notes (optional)"
          value={formData.notes}
          onChange={handleChange('notes')}
          multiline
          rows={2}
          placeholder="Any additional information for attendees..."
          disabled={submitting}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !formData.date || !formData.type}
        >
          {submitting ? 'Scheduling...' : isEditing ? 'Update Meeting' : 'Schedule Meeting'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleMeetingModal;
